/**
 * Interaction Tracking Service (TRAC-258)
 *
 * Fire-and-forget tracking of user interactions with chat UI elements.
 * Two operations:
 *   - createImpression: called when an interactive element renders
 *   - appendClick: called on each user click/toggle/selection
 *
 * Guards against duplicate impressions from React re-renders using
 * a Set of "sessionId:sortKey" keys.
 */

import { getBackendApiUrl } from '../config/backend';
import { logger } from '../utils/logger';

/**
 * TRAC-790: which kind of Tire Rack page the chat is sitting on.
 *
 * The in-chat events (a starter prompt shown, a tire card clicked) could not be
 * grouped by page at all, so "which prompts get used on the cart page" was
 * unanswerable for exactly the events that would answer it, while every page-level
 * event has carried page_kind from the start. Held as a module value and set from the
 * page context rather than read from the chat service, to avoid the two importing each
 * other for one string.
 */
let currentPageKind: string | null = null;
export function setTrackingPageKind(kind: string | null | undefined): void {
  currentPageKind = kind || null;
}
/**
 * TRAC-928: the live-person funnel reports by page too, and reads the same value
 * rather than keeping its own copy that could drift from this one.
 */
export function getTrackingPageKind(): string | null {
  return currentPageKind;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// TRAC-527: Surface names. The action ("impression" vs "click") lives in
// the trace `name` field, so an event_type like `tdg_link_click` paired
// with `name="impression"` reads as "impression of a click event" — a
// rename to surface-only fixes that. Pre-rename strings (for migrating
// dashboards / queries):
//   selection_card    ← card_selection
//   source_card       ← source_card_click
//   tdg_chip          ← tdg_chip_toggle
//   tdg_season        ← tdg_season_select
//   tdg_offroad       ← tdg_offroad_toggle
//   tdg_runflat       ← tdg_runflat_toggle
//   tdg_link          ← tdg_link_click
//   inline_link       ← inline_link_click
//   ymmv_vehicle      ← ymmv_vehicle_select
//   ymmv_year         ← ymmv_year_select
//   ymmv_make         ← ymmv_make_select
//   ymmv_model_family ← ymmv_model_family_select
//   ymmv_tire_size    ← ymmv_tire_size_select
//   tire_card         ← tire_card_click
//   chat_button       ← chat_button_click
export type InteractionEventType =
  | 'starter_prompt'
  | 'selection_card'
  | 'tdg_chip'
  | 'tdg_season'
  | 'tdg_offroad'
  | 'tdg_runflat'
  | 'tdg_link'
  | 'source_card'
  | 'inline_link'
  | 'vehicle_header_shop_tires'
  | 'vehicle_header_select_tires'
  | 'vehicle_header_add_size'
  | 'vehicle_header_switch_vehicle'
  | 'vehicle_header_new_vehicle'
  | 'ymmv_vehicle'
  | 'ymmv_year'
  | 'ymmv_make'
  | 'ymmv_model_family'
  | 'ymmv_tire_size'
  | 'tire_card'
  | 'sales_contact_call'
  | 'sales_contact_form'
  | 'chat_button';

export interface ImpressionParams {
  sessionId: string;
  turnNumber: number;
  eventType: InteractionEventType;
  // Each option is either a label string or an object carrying the label
  // plus any extras the rendering component attached (URL, productUrl,
  // linkType, etc.). Letting impressions carry rich option payloads
  // means dashboards can see *what the user saw* — not just the labels.
  optionsShown: Array<string | Record<string, unknown>>;
  metadata?: Record<string, unknown>;
}

export interface ClickParams {
  sessionId: string;
  sortKey: string;
  interaction: {
    selected: string;
    timestamp: string;
    [key: string]: unknown; // newValue, url, linkType, previousSeason, etc.
  };
  /**
   * TRAC-518 Phase 8a: mirror the parent impression's eventType +
   * turnNumber on the click payload. Before Phase 8a the backend
   * resolved these via a DynamoDB round-trip on the impression row; now
   * the frontend passes them directly so the click endpoint stays pure
   * Langfuse-write without any DDB reads.
   */
  eventType: string;
  turnNumber: number;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

/** Monotonic counter for generating unique sort keys within a session */
let sequenceCounter = 0;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Generate a sort key: ISO timestamp + 5-digit sequence.
 * e.g., "2026-02-16T17:00:05.123Z#00001"
 */
function generateSortKey(): string {
  sequenceCounter += 1;
  const seq = String(sequenceCounter).padStart(5, '0');
  return `${new Date().toISOString()}#${seq}`;
}

// ---------------------------------------------------------------------------
// API calls (fire-and-forget)
// ---------------------------------------------------------------------------

async function postImpression(
  params: ImpressionParams,
  sortKey: string,
  getToken: () => Promise<string | undefined>
): Promise<void> {
  try {
    const token = await getToken();
    await fetch(`${getBackendApiUrl()}/api/analytics/interaction-events/impression`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        sessionId: params.sessionId,
        sortKey,
        eventType: params.eventType,
        turnNumber: params.turnNumber,
        optionsShown: params.optionsShown,
        pageKind: currentPageKind,
        metadata: params.metadata || {},
      }),
      // TRAC-527: no `keepalive` here. Impressions fire when an element
      // renders, not on user interaction that might race navigation, so
      // we don't need the unload protection. Chrome's keepalive queue has
      // been observed to stall requests under bursts (one-off pending
      // fetch with 0 bytes transferred) — regular fetch avoids that pool.
    });
  } catch (error) {
    // Fire-and-forget — log but don't throw
    logger.error('Failed to create impression:', error);
  }
}

async function postClick(
  params: ClickParams,
  getToken: () => Promise<string | undefined>
): Promise<void> {
  try {
    const token = await getToken();
    await fetch(`${getBackendApiUrl()}/api/analytics/interaction-events/click`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        sessionId: params.sessionId,
        sortKey: params.sortKey,
        interaction: params.interaction,
        eventType: params.eventType,
        turnNumber: params.turnNumber,
        pageKind: currentPageKind,
      }),
      // TRAC-527: see keepalive note on postImpression — clicks are even
      // more likely to race against page unload (user clicks a link and
      // navigates in the same tick).
      keepalive: true,
    });
  } catch (error) {
    logger.error('Failed to append click:', error);
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create an impression record when an interactive element renders.
 * Returns the sortKey needed to later append clicks to this record.
 *
 * No dedup here — callers (e.g. ChatTrackingRoot) own dedup via
 * IntersectionObserver + element-identity caching, which is the right
 * granularity (one impression per DOM element instance, allowing the
 * same eventType/turnNumber to recur when a typeahead is re-opened or
 * a drill-down step is revisited).
 *
 * Fire-and-forget: the POST kicks off and this returns immediately.
 * Langfuse ingest order is handled server-side — the backend parses the
 * sortKey's ISO timestamp and pins the trace's start_time to it, so
 * parallel/racing POSTs still land in client-order in the Langfuse UI.
 * See `_emit_with_client_timestamp` in backend/src/services/langfuse_service.py.
 */
export function createImpression(
  params: ImpressionParams,
  getToken: () => Promise<string | undefined>
): string {
  const sortKey = generateSortKey();
  postImpression(params, sortKey, getToken);
  return sortKey;
}

/**
 * Append a click/interaction to an existing impression record.
 * sortKey must match a previously created impression.
 */
export function appendClick(
  params: ClickParams,
  getToken: () => Promise<string | undefined>
): void {
  // Fire and forget
  postClick(params, getToken);
}

/**
 * Reset state. Call when starting a new session so the sequence counter
 * restarts from #00001 (readable in Langfuse session views).
 */
export function resetTrackingState(): void {
  sequenceCounter = 0;
}
